/**
 * filter-popover.tsx — Nút phễu "Lọc" ở đầu danh sách hội thoại (kiểu SaleSmartly).
 *
 * Sửa điều kiện ở đây chỉ có hiệu lực TẠM trên trang: chuyển chế độ xem, làm
 * mới hay rời trang là về lại điều kiện gốc. Muốn giữ → "Lưu Chế độ xem mới".
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Filter, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/misc'
import { cn } from '@/lib/utils'
import { AddConditionButton, ConditionRows } from './condition-editor'
import { cleanConditions, newCondition, type ConditionLogic, type InboxCondition } from './inbox-conditions'

export interface FilterPopoverProps {
  /** Điều kiện đang áp dụng (của chế độ xem + sửa tạm). */
  conditions: InboxCondition[]
  logic: ConditionLogic
  /** Điều kiện gốc của chế độ xem đang chọn (rỗng nếu là bộ lọc có sẵn). */
  baseConditions: InboxCondition[]
  baseLogic: ConditionLogic
  onApply: (conditions: InboxCondition[], logic: ConditionLogic) => void
  /** Mở hộp thoại lưu thành chế độ xem mới với bộ điều kiện đang soạn. */
  onSaveAsView: (conditions: InboxCondition[], logic: ConditionLogic) => void
}

export function FilterPopover({ conditions, logic, baseConditions, baseLogic, onApply, onSaveAsView }: FilterPopoverProps) {
  const { t } = useTranslation('convList')
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<InboxCondition[]>([])
  const [draftLogic, setDraftLogic] = useState<ConditionLogic>('and')
  const active = cleanConditions(conditions).length

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // Mở ra là bản sao của điều kiện đang áp dụng; chưa có gì thì sẵn một hàng trống.
      setDraft(conditions.length > 0 ? conditions.map((c) => ({ ...c })) : [newCondition()])
      setDraftLogic(logic)
    }
    setOpen(next)
  }

  const apply = () => {
    onApply(cleanConditions(draft), draftLogic)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={active ? t('filterPopover.filterWithCount', { count: active }) : t('filterPopover.filter')}
              className={cn('relative shrink-0', active > 0 && 'bg-accent text-primary hover:bg-accent')}
            >
              <Filter className="h-4 w-4" />
              {active > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
                  {active}
                </span>
              )}
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{t('filterPopover.filter')}</TooltipContent>
      </Tooltip>

      <PopoverContent align="start" className="w-[760px] max-w-[calc(100vw-16px)] p-0">
        <div className="border-b px-4 py-3">
          <h3 className="text-base font-medium">{t('filterPopover.filter')}</h3>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {t('filterPopover.note')}
          </p>
        </div>
        <div className="max-h-[50vh] overflow-y-auto px-4 py-3">
          <ConditionRows conditions={draft} logic={draftLogic} onChange={setDraft} onLogicChange={setDraftLogic} />
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t px-4 py-3">
          <AddConditionButton onAdd={(c) => setDraft((d) => [...d, c])} />
          <button
            type="button"
            onClick={() => {
              setDraft(baseConditions.map((c) => ({ ...c })))
              setDraftLogic(baseLogic)
            }}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-4 w-4" />
            {t('filterPopover.restore')}
          </button>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant="outline"
              disabled={cleanConditions(draft).length === 0}
              onClick={() => {
                setOpen(false)
                onSaveAsView(cleanConditions(draft), draftLogic)
              }}
            >
              {t('filterPopover.saveAsView')}
            </Button>
            <Button onClick={apply}>{t('filterPopover.filter')}</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
